import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { TutorCategoryType } from '@brighterly/lib-core-types/tutor/enums'
import type { LessonTutorFilters } from './interfaces'

import { useListsStore } from '@shared/model'

import { useDisciplineStore } from '../store'
import { buildTutorFilterOptions, countTutorFilters, getDefaultTutorFilters } from './lesson-create'

interface LessonTutorFiltersOptions {
  /** The filters in force when the panel opens again; the discipline's defaults otherwise. */
  initial?: LessonTutorFilters | null
  onApply: (filters: LessonTutorFilters) => void
  onClear: () => void
}

interface LessonTutorFiltersReturn {
  refIsOpen: Ref<boolean>
  refCount: ComputedRef<number>
  refGender: Ref<string[]>
  refSpecialFeatures: Ref<string[]>
  refAccent: Ref<string[]>
  refCategories: Ref<string[]>
  refGenderOptions: ComputedRef<AppSelectOption<string>[]>
  refSpecialFeatureOptions: ComputedRef<AppSelectOption<string>[]>
  refAccentOptions: ComputedRef<AppSelectOption<string>[]>
  refCategoryOptions: ComputedRef<AppSelectOption<string>[]>
  toggle: () => void
  apply: () => void
  clear: () => void
}

/** The tutor filters of a lesson form edit a draft; the form gets them only once they are applied, and keeps them for
 *  every following change until Clear. A panel that folds opens closed and folds back on Apply and Clear, with the
 *  count of the filters drafted on its fold. */
export const useLessonTutorFilters = ({ initial = null, onApply, onClear }: LessonTutorFiltersOptions): LessonTutorFiltersReturn => {
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()

  const getDefaults = (): LessonTutorFilters => getDefaultTutorFilters(disciplineStore.getDiscipline?.code)

  const start = initial ?? getDefaults()

  const refIsOpen = ref<boolean>(false)
  const refGender = ref<string[]>([...start.gender])
  const refSpecialFeatures = ref<string[]>([...start.special_features])
  const refAccent = ref<string[]>([...start.accent])
  const refCategories = ref<string[]>([...start.categories])

  const refGenderOptions = computed<AppSelectOption<string>[]>(() => buildTutorFilterOptions(listsStore.getLists?.tutor.gender))
  const refSpecialFeatureOptions = computed<AppSelectOption<string>[]>(() => buildTutorFilterOptions(listsStore.getLists?.tutor.special_feature))
  const refAccentOptions = computed<AppSelectOption<string>[]>(() => buildTutorFilterOptions(listsStore.getLists?.tutor.accent))
  const refCategoryOptions = computed<AppSelectOption<string>[]>(() => buildTutorFilterOptions(listsStore.getLists?.tutor.category))

  const getDraft = (): LessonTutorFilters => ({
    gender: [...refGender.value],
    special_features: [...refSpecialFeatures.value],
    accent: [...refAccent.value],
    categories: [...refCategories.value] as TutorCategoryType[],
  })

  const refCount = computed<number>(() => countTutorFilters(getDraft()))

  const resetDraft = (): void => {
    const filters = getDefaults()

    refGender.value = [...filters.gender]
    refSpecialFeatures.value = [...filters.special_features]
    refAccent.value = [...filters.accent]
    refCategories.value = [...filters.categories]
  }

  const toggle = (): void => {
    refIsOpen.value = !refIsOpen.value
  }

  const apply = (): void => {
    onApply(getDraft())
    refIsOpen.value = false
  }

  const clear = (): void => {
    resetDraft()
    onClear()
    refIsOpen.value = false
  }

  return {
    refIsOpen,
    refCount,
    refGender,
    refSpecialFeatures,
    refAccent,
    refCategories,
    refGenderOptions,
    refSpecialFeatureOptions,
    refAccentOptions,
    refCategoryOptions,
    toggle,
    apply,
    clear,
  }
}
